/**
 * Single source of truth for deriving the Job Docs "operation context"
 * (`partNumber` / `operationId` / `partOperationRef`) from a work-order-op or
 * a legacy job.
 *
 * ONE source per call — the three fields always describe the same operation:
 *
 *  - With an `op`, everything comes from the op; job terms are inert. Part
 *    number precedence:
 *      1. nested `op.partOperation.partNumber` — currentWorkOrderOp (KHub/ERP)
 *      2. `op.workOrder.partNumber`            — APM synthetic current op
 *    Ref precedence: `op.partOperationRef` → nested
 *    `op.partOperation.partOperationRef`.
 *  - Without an op, everything comes from the job (`part`, `operation`,
 *    `partOperationRef`).
 *
 * An op missing a field resolves that field to `undefined` instead of
 * borrowing the job's, so callers hide or disable rather than build a
 * mixed-identity URL.
 *
 * Op SELECTION and `activityMode` stay with the caller: the op source genuinely
 * differs per screen (job-only, getCurrentWorkOrderOp, or workOrderOp ||
 * currentWorkOrderOp) and so does activityMode. JobInfo must call with `op`
 * undefined: a selected job is never described by the running op.
 *
 * `??` throughout so a numeric `0` is preserved (0 is a value, not absence).
 *
 * @param {object} [op] Work-order-op (may carry `partOperation`, `workOrder`,
 *   `sequenceNumber`, `partOperationRef`). Pass `undefined` for the job-only
 *   (legacy) flow.
 * @param {object} [extras]
 * @param {object} [extras.job] Legacy job (`{ part, operation, partOperationRef }`).
 * @returns {{ partNumber: string|number|undefined,
 *   operationId: string|number|undefined,
 *   partOperationRef: string|number|undefined }}
 */
export function resolveJobDocsContext(op, { job } = {}) {
  if (op == null) {
    return {
      partNumber: job?.part,
      operationId: job?.operation,
      partOperationRef: job?.partOperationRef,
    };
  }
  return {
    partNumber: op.partOperation?.partNumber ?? op.workOrder?.partNumber,
    operationId: op.sequenceNumber,
    partOperationRef: op.partOperationRef ?? op.partOperation?.partOperationRef,
  };
}
